import { Typography } from "@material-ui/core";
import DataTable from 'components/DataTable/DataTable';
import DataListHeader from 'components/DataListHeader';
import { useTitle } from "hooks/useTitle";
import { orderBy } from 'lodash';
import { useMemo, useState } from 'react';
import FilterService, { fieldTypes } from 'services/filterService';
import { aggregateGradeDelta, gradeDeltaClients, gradeDeltaSoldVehicles } from "./gradeDeltaData";
import { GRADE_COLORS, gradeDeltaDashboardStyles } from "./GradeDeltaDashboardStyles";

const numberFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const formatSAR = (val) => numberFormatter.format(val);
const formatSARLabel = (val) => `SAR ${numberFormatter.format(val)}`;
const formatPct = (val) => `${(val * 100).toFixed(1)}%`;
const gradeColor = (grade) => GRADE_COLORS[grade] || '#607d8b';

const EMPTY_FILTERS = { client: { value: null }, soldDate: { value: null } };

const dropDownsConfig = [
    {
        title: 'Client',
        key: 'client',
        type: fieldTypes.TEXT,
        values: gradeDeltaClients.map((c) => ({ id: c, name: c })),
        size: 2,
    },
    {
        title: 'Sold Date',
        key: 'soldDate',
        type: fieldTypes.DATERANGE,
        size: 3,
    },
];

const GradeCard = ({ row, classes }) => {
    const color = gradeColor(row.grade);
    return (
        <div className={classes.gradeCard}>
            <div className={classes.gradeCardBar} style={{ backgroundColor: color }} />
            <div className={classes.gradeCardBody}>
                <Typography className={classes.gradeCardLabel} style={{ color }}>
                    Grade {row.grade}
                </Typography>
                <Typography className={classes.gradeCardPrice}>
                    {formatSARLabel(row.avgSoldPrice)}
                </Typography>
                <Typography className={classes.gradeCardVsAppraisal}>
                    vs. appraisal {formatPct(row.avgVsAppraisalPct)}
                </Typography>
            </div>
        </div>
    );
};

const GradeDeltaDashboardPage = () => {
    const classes = gradeDeltaDashboardStyles();
    useTitle('Grade Delta Intelligence');

    const [columnToSort, setColumnToSort] = useState('grade');
    const [sortDirection, setSortDirection] = useState('asc');
    const [filters, setFilters] = useState(EMPTY_FILTERS);

    const onDropFilter = (value, key) => setFilters((prev) => ({ ...prev, [key]: { value } }));

    const filteredRecords = useMemo(
        () => FilterService.filter(filters, '', gradeDeltaSoldVehicles, dropDownsConfig),
        [filters],
    );

    const allRows = useMemo(() => aggregateGradeDelta(filteredRecords), [filteredRecords]);
    const rows = orderBy(allRows, [columnToSort], [sortDirection]);

    const onSort = (col) => {
        if (col === columnToSort) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setColumnToSort(col);
            setSortDirection('asc');
        }
    };

    const columns = [
        {
            name: 'Grade',
            key: 'grade',
            component: (row) => {
                const color = gradeColor(row.grade);
                return (
                    <div className={classes.gradeIndicator}>
                        <div className={classes.gradeColorBar} style={{ backgroundColor: color }} />
                        <span className={classes.gradeValue} style={{ color }}>{row.grade}</span>
                    </div>
                );
            },
        },
        { name: 'Vehicles Sold', key: 'vehiclesSold' },
        { name: 'Avg Appraisal (SAR)', key: 'avgAppraisal', component: (row) => formatSAR(row.avgAppraisal) },
        {
            name: 'Avg Sold (SAR)',
            key: 'avgSoldPrice',
            component: (row) => <span className={classes.soldPriceCell}>{formatSAR(row.avgSoldPrice)}</span>,
        },
        { name: 'Avg Service Cost (SAR)', key: 'avgServiceCost', component: (row) => formatSAR(row.avgServiceCost) },
        {
            name: 'Avg Net-to-Client (SAR)',
            key: 'avgNetToClient',
            component: (row) => <span className={classes.netToClientCell}>{formatSAR(row.avgNetToClient)}</span>,
        },
        { name: 'Avg Days on Yard', key: 'avgDaysOnYard' },
    ];

    const mapExportData = (row) => ({
        'Grade': row.grade,
        'Vehicles Sold': row.vehiclesSold,
        'Avg Appraisal (SAR)': formatSAR(row.avgAppraisal),
        'Avg Sold (SAR)': formatSAR(row.avgSoldPrice),
        'Avg Service Cost (SAR)': formatSAR(row.avgServiceCost),
        'Avg Net-to-Client (SAR)': formatSAR(row.avgNetToClient),
        'Avg Days on Yard': row.avgDaysOnYard,
    });

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <Typography className={classes.title}>Grade Delta Intelligence</Typography>
                <Typography className={classes.subtitle}>
                    Vehicle auction outcomes by condition grade — sold price, service cost, and net-to-client value per grade band.
                </Typography>
            </div>

            {allRows.length > 0 && (
                <div className={classes.cardsRow}>
                    {allRows.map((row) => (
                        <GradeCard key={row.grade} row={row} classes={classes} />
                    ))}
                </div>
            )}

            <DataListHeader
                dropDownsConfig={dropDownsConfig}
                filters={filters}
                onDropFilter={onDropFilter}
            />
            <DataTable
                rows={rows}
                columns={columns}
                onSort={onSort}
                sortDirection={sortDirection}
                columnToSort={columnToSort}
                mapExportData={mapExportData}
                noItemsMessage="No grade data available"
                rowIdentifier="grade"
            />
        </div>
    );
};

export default GradeDeltaDashboardPage;
